import type {Placement} from '@floating-ui/vue'
import {getPlacementSide} from './getPlacementSide'

export interface ReferenceRect {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Available size in the gap between the reference and the viewport on the
 * placed side:
 *
 *   [trigger][offset][*availableSpace*][viewportPadding][viewport]
 *
 * Never includes space on the opposite side of the trigger (that would let the
 * panel cover the reference). Cross-axis budget is the full viewport so
 * `shift` on the alignment axis can keep the panel in view.
 */
export function getReferenceAnchoredAvailableSize(
  reference: ReferenceRect,
  placement: Placement,
  viewportPadding = 8,
  offsetMainAxis = 0,
): {availableHeight: number, availableWidth: number} {
  const side = getPlacementSide(placement)
  const spaceBelow = window.innerHeight - reference.y - reference.height - viewportPadding - offsetMainAxis
  const spaceAbove = reference.y - viewportPadding - offsetMainAxis
  const spaceRight = window.innerWidth - reference.x - reference.width - viewportPadding - offsetMainAxis
  const spaceLeft = reference.x - viewportPadding - offsetMainAxis
  const viewportWidth = window.innerWidth - viewportPadding * 2

  if (side === 'top') {
    return {
      availableHeight: Math.max(spaceAbove, 0),
      availableWidth: Math.max(viewportWidth, 0),
    }
  }

  if (side === 'bottom') {
    return {
      availableHeight: Math.max(spaceBelow, 0),
      availableWidth: Math.max(viewportWidth, 0),
    }
  }

  const availableWidth = Math.max(side === 'left' ? spaceLeft : spaceRight, 0)

  // Vertical is the alignment axis here: the panel sits beside the trigger, so
  // it never covers it, and `shift` keeps it inside the viewport.
  const availableHeight = Math.max(window.innerHeight - viewportPadding * 2, 0)

  return {
    availableHeight,
    availableWidth,
  }
}
